import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, inject, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MultisiteService } from '@df/core/service/multisite';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { AdvancedCommerceConfigStore } from '@df/module-advanced-commerce/store/advanced-commerce-config.store';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { ImageModule } from '@df/module-image/image.module';
import { IfStandaloneDirective } from '@df/module-pwa/utils/if-standalone.directive';
import { SearchChangeEvent } from '@df/search/search.component.def';
import { IfLoggedInDirective } from '@df/session/customer/if-logged-in.directive';
import { IfNotLoggedInDirective } from '@df/session/customer/if-not-logged-in.directive';
import { ButtonsOptions, MarginTopOptions, RatioOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ProductComponent } from '../../catalog/ui/product/product.component';
import { AdvancedCommerceSearchFormComponent } from '../../module/advanced-commerce/search/form/advanced-commerce-search-form.component';
import { AdvancedCommerceSearchOutletComponent } from '../../module/advanced-commerce/search/outlet/advanced-commerce-search-outlet.component';
import { MultisiteLabelComponent } from '../../multisite/label/multisite-label.component';
import { NMultisiteModal } from '../../multisite/modal/multisite-modal.interfaces';
import { TradeStoreService } from '../../multisite/trade/trade-store.service';
import { SearchOutletComponent } from '../../search/ui/outlet/search-outlet.component';
import { SearchComponent } from '../../search/ui/search.component';
import { ExpandableComponent } from '../../ui/display/expandable/expandable.component';
import { PageFooterComponent } from '../page/footer/page-footer.component';
import { OffCanvas } from './off-canvas-menu-body.interface';

let uid = 0;

const getColorsFields = <D>(canShow: (data: D) => boolean): NICmsContentField.IField[] => [
    <NICmsContentField.IChoiceField>{
        key: 'colorColumns',
        type: NICmsContentField.EFieldType.choice,
        title: 'Color columns',
        options: [
            { key: 'one-col', title: 'One column' },
            { key: 'two-cols', title: 'Two columns' }
        ],
        default: 'one-col',
        canShow
    },
    <NICmsContentField.IColorPickerField>{
        key: 'color',
        type: NICmsContentField.EFieldType.colorPicker,
        title: 'Colour',
        canShow
    },
    <NICmsContentField.IField>{
        key: 'isNewColor',
        type: NICmsContentField.EFieldType.toggle,
        title: 'Is a new colour',
        required: true,
        default: false,
        canShow
    },
    <NICmsContentField.IColorPickerField>{
        key: 'newTextColor',
        type: NICmsContentField.EFieldType.colorPicker,
        title: 'New text colour',
        canShow: data => canShow(data) && data?.isNewColor
    }
];
@Component({
    selector: 'off-canvas-menu-body',
    templateUrl: 'off-canvas-menu-body.html',
    styleUrls: ['off-canvas-menu-body.scss'],
    standalone: true,
    imports: [
        AdvancedCommerceSearchFormComponent,
        AdvancedCommerceSearchOutletComponent,
        ExpandableComponent,
        Ga4TrackClickDirective,
        HrefComponent,
        ICmsClientModule,
        IfLoggedInDirective,
        IfNotLoggedInDirective,
        ImageModule,
        MultisiteLabelComponent,
        NgClass,
        NgStyle,
        forwardRef(() => ProductComponent),
        RouterLink,
        SearchComponent,
        SearchOutletComponent,
        ShellComponent,
        TranslationModule,
        ZippyModule,
        PageFooterComponent,
        IfStandaloneDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<OffCanvasMenuBodyComponent>({
    id: 'off-canvas-menu',
    block: {
        url: 'off-canvas-menu'
    },
    name: 'Off Canvas Menu',
    description: 'Off canvas menu component',
    onDemandOnly: true,
    fields: [
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            complex: true,
            key: 'menu',
            title: 'Menu Items',
            itemName: 'Item',
            itemNameField: 'text',
            defaultSize: 3,
            fields: [
                <NICmsContentField.IField>{
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Item text',
                    default: 'Some link',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    translatable: false,
                    title: 'Link'
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image'
                },
                <NICmsContentField.IField>{
                    key: 'secondLevelTurnOn',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Level 2 turn on',
                    default: false
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    complex: true,
                    key: 'secondLevelItems',
                    title: 'Level 2 Items',
                    itemName: 'Item',
                    itemNameField: 'title',
                    fields: [
                        <NICmsContentField.IField>{
                            key: 'title',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Title',
                            default: 'Lorem ipsum'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'titleStyle',
                            type: NICmsContentField.EFieldType.choice,
                            options: TextStyleOptions,
                            title: 'Title style'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'marginTop',
                            type: NICmsContentField.EFieldType.choice,
                            options: MarginTopOptions,
                            title: 'Margin top'
                        },
                        <NICmsContentField.IRepeaterField>{
                            type: NICmsContentField.EFieldType.repeater,
                            complex: true,
                            key: 'list',
                            title: 'List',
                            itemName: 'Item',
                            itemNameField: 'text',
                            defaultSize: 1,
                            fields: [
                                <NICmsContentField.IChoiceField>{
                                    key: 'listItemVariant',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'List variant',
                                    default: 'text-list',
                                    options: [
                                        { key: 'text-list', title: 'Text list' },
                                        { key: 'colors', title: 'Colours list' },
                                        { key: 'products', title: 'Products' },
                                        { key: 'images-list', title: 'Images list' },
                                        { key: 'submenu', title: 'Submenu list' },
                                        { key: 'accordions', title: 'Accordions list' }
                                    ]
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'textItemVariant',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'Item variant',
                                    options: [
                                        { key: 'text', title: 'Text' },
                                        { key: 'button', title: 'Button' }
                                    ],
                                    default: 'text',
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) => data?.listItemVariant === 'text-list'
                                },
                                {
                                    key: 'image',
                                    type: NICmsContentField.EFieldType.image,
                                    title: 'Image',
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) => data?.listItemVariant === 'images-list'
                                },
                                <NICmsContentField.IField>{
                                    key: 'text',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Item text',
                                    default: 'Lorem ipsum dolor',
                                    required: true,
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) => data?.listItemVariant !== 'products'
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'textStyle',
                                    type: NICmsContentField.EFieldType.choice,
                                    options: TextStyleOptions,
                                    title: 'Text style',
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) =>
                                        (data?.textItemVariant !== 'button' && data?.textItemVariant === 'button') ||
                                        data?.listItemVariant !== 'products'
                                },
                                <NICmsContentField.IField>{
                                    key: 'imageCta',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Button',
                                    default: 'Lorem ipsum',
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) => data?.listItemVariant === 'images-list'
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'ctaStyle',
                                    type: NICmsContentField.EFieldType.choice,
                                    options: ButtonsOptions,
                                    title: 'Button style',
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) =>
                                        (data?.listItemVariant === 'text-list' && data?.textItemVariant === 'button') ||
                                        data?.listItemVariant === 'images-list'
                                },
                                <NICmsContentField.IField>{
                                    key: 'link',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Link',
                                    translatable: false,
                                    default: '/',
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) =>
                                        data?.listItemVariant !== 'products' &&
                                        data?.listItemVariant !== 'submenu' &&
                                        data?.listItemVariant !== 'accordions'
                                },
                                ...getColorsFields<OffCanvas.IOffCanvasModalMenuListData>(data => data?.listItemVariant === 'colors'),
                                <NICmsContentField.IRelationField>{
                                    key: 'products',
                                    type: NICmsContentField.EFieldType.relation,
                                    title: 'Products',
                                    options: {
                                        title: 'Product',
                                        type: 'product',
                                        qty: 5,
                                        multiple: true
                                    },
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) => data?.listItemVariant === 'products'
                                },
                                <NICmsContentField.IRepeaterField>{
                                    type: NICmsContentField.EFieldType.repeater,
                                    complex: true,
                                    key: 'thirdLevelItems',
                                    title: 'Level 3 Items',
                                    itemName: 'Item',
                                    itemNameField: 'text',
                                    canShow: (data: OffCanvas.IOffCanvasModalMenuListData) =>
                                        data?.listItemVariant === 'submenu' || data?.listItemVariant === 'accordions',
                                    fields: [
                                        <NICmsContentField.IChoiceField>{
                                            key: 'variant',
                                            type: NICmsContentField.EFieldType.choice,
                                            title: 'Variant',
                                            default: 'text',
                                            options: [
                                                { key: 'text', title: 'Text' },
                                                { key: 'colors', title: 'Colours list' }
                                            ]
                                        },
                                        <NICmsContentField.IField>{
                                            key: 'icon',
                                            type: NICmsContentField.EFieldType.image,
                                            title: 'Icon',
                                            canShow: (data: OffCanvas.IOffCanvasModalMenuThirdLevelItem) => data?.variant === 'text'
                                        },
                                        <NICmsContentField.IField>{
                                            key: 'text',
                                            type: NICmsContentField.EFieldType.text,
                                            title: 'Text',
                                            default: 'Lorem ipsum'
                                        },
                                        <NICmsContentField.IChoiceField>{
                                            key: 'textStyle',
                                            type: NICmsContentField.EFieldType.choice,
                                            options: TextStyleOptions,
                                            title: 'Text style'
                                        },
                                        <NICmsContentField.IField>{
                                            key: 'textLink',
                                            type: NICmsContentField.EFieldType.text,
                                            title: 'Text link',
                                            translatable: false,
                                            default: '/'
                                        },
                                        ...getColorsFields<OffCanvas.IOffCanvasModalMenuThirdLevelItem>(data => data?.variant === 'colors')
                                    ]
                                }
                            ]
                        }
                    ],
                    canShow: data => data?.secondLevelTurnOn
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    complex: true,
                    key: 'imageSection',
                    title: 'Image section',
                    itemName: 'Item',
                    itemNameField: 'imageTitle',
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'marginTop',
                            type: NICmsContentField.EFieldType.choice,
                            options: MarginTopOptions,
                            title: 'Margin top'
                        },
                        {
                            key: 'image',
                            type: NICmsContentField.EFieldType.image,
                            title: 'Image'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'imageRatio',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Image Ratio',
                            options: RatioOptions,
                            required: true
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'imageTextVariant',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Image text variant',
                            options: [
                                { key: 'below', title: 'Below' },
                                { key: 'overlap', title: 'Overlap' }
                            ],
                            default: 'below'
                        },
                        {
                            key: 'imageTextBackgroundColor',
                            type: NICmsContentField.EFieldType.colorPicker,
                            title: 'Background Color',
                            previewMode: 'color',
                            canShow: (data: OffCanvas.IOffCanvasModalMenuImage) => data?.imageTextVariant === 'overlap'
                        },
                        {
                            key: 'imageTextColor',
                            type: NICmsContentField.EFieldType.colorPicker,
                            title: 'Text Color',
                            previewMode: 'color',
                            canShow: (data: OffCanvas.IOffCanvasModalMenuImage) => data?.imageTextVariant === 'overlap'
                        },
                        <NICmsContentField.IField>{
                            key: 'imageTitle',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Image title',
                            default: 'Lorem ipsum'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'imageTitleStyle',
                            type: NICmsContentField.EFieldType.choice,
                            options: TextStyleOptions,
                            title: 'Title style'
                        },
                        <NICmsContentField.IField>{
                            key: 'imageSubtitle',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Image subtitle',
                            default: 'Dolor sit amet'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'imageSubtitleStyle',
                            type: NICmsContentField.EFieldType.choice,
                            options: TextStyleOptions,
                            title: 'Subtitle style'
                        },
                        <NICmsContentField.IField>{
                            key: 'imageLink',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Image link',
                            default: '/'
                        }
                    ],
                    canShow: data => data?.secondLevelTurnOn
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            complex: true,
            key: 'footer_links',
            title: 'Footer Links',
            itemName: 'Item',
            itemNameField: 'text',
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'isMultisite',
                    title: 'Type of field',
                    default: 'no',
                    options: [
                        {
                            key: 'no',
                            title: 'Default field'
                        },
                        {
                            key: 'yes',
                            title: 'Multisite field'
                        }
                    ]
                },
                <NICmsContentField.IField>{
                    key: 'icon',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Icon',
                    canShow: (data: OffCanvas.IOffCanvasModalMenuFooterLinkItem) => data?.isMultisite === 'no'
                },
                <NICmsContentField.IField>{
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Item text',
                    default: 'Some link',
                    canShow: (data: OffCanvas.IOffCanvasModalMenuFooterLinkItem) => data?.isMultisite === 'no'
                },
                <NICmsContentField.IField>{
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    canShow: (data: OffCanvas.IOffCanvasModalMenuFooterLinkItem) => data?.isMultisite === 'no'
                }
            ]
        }
    ]
})
export class OffCanvasMenuBodyComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: OffCanvas.IOffCanvasModalBlockContent;

    @Output()
    hide = new EventEmitter<MethodDecorator>();

    private tradeStoreService = inject(TradeStoreService);
    private multisite = inject(MultisiteService);

    protected enabledAc = !!inject(AdvancedCommerceConfigStore)?.config()?.enabled;

    searchTrayVisible = false;

    private readonly _uid = `offCanvasMenu${uid++}`;

    protected readonly multisiteModalId = NMultisiteModal.componentId;

    protected searchClosedHandler(event: boolean): void {
        if (event) {
            this.hideOffCanvas();
        }
    }

    hideOffCanvas(): void {
        if (this.searchTrayVisible) {
            this.searchTrayVisible = false;
            this.detectChanges();
        } else {
            this.hide.emit();
        }
    }

    onSearchFocusChange(event: SearchChangeEvent): void {
        if (event.value) {
            this.searchTrayVisible = true;
            this.detectChanges();
        }
    }

    getZippyId(index: number | string) {
        return [this.uid, index];
    }

    get uid(): string {
        return this._uid;
    }

    protected getNavigationGA4Data(navParent?: string, navLvl?: string, navItem?: string): Gtm.IGA4TrackData {
        return {
            name: 'navigation_engagement',
            details: { nav_parent: navParent, nav_lvl2: navLvl, nav_item: navItem }
        };
    }

    get hideMultisteRetailSelector(): boolean {
        return !this.tradeStoreService.retailSites.filter(site => site.siteId !== this.multisite.current.siteId)?.length;
    }
}
